// bookie-amber-preview — Quyosh Nuri catalog card (bk11).
//
// Warm amber bookie: tinted outer ring with ornamental compass-corner
// flourishes, ivory inner page, amber sunburst medallion when no photo
// is set, "TAKLIFNOMA" small-cap eyebrow. Pulls every colour from
// LayoutCtx so the same component renders any amber-family bookie.
import { Photo, type LayoutCtx } from "./_shared";

export function BookieAmberPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(160deg, ${accent}33 0%, ${accent}11 50%, ${accent}33 100%)`,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 5px ${bg}, inset 0 0 0 6px ${accent}33`,
        }}
      >
        {/* Corner ornaments — four mirrored compass marks */}
        {[
          { top: "4%", left: "4%", r: 0 },
          { top: "4%", right: "4%", r: 90 },
          { bottom: "4%", right: "4%", r: 180 },
          { bottom: "4%", left: "4%", r: 270 },
        ].map((p, i) => (
          <svg
            key={i}
            viewBox="0 0 20 20"
            style={{ position: "absolute", width: 14, height: 14, transform: `rotate(${p.r}deg)`, ...p }}
            aria-hidden
          >
            <path d="M2 2 L10 2 M2 2 L2 10 M2 6 Q 6 6 6 2" fill="none" stroke={accent} strokeWidth="0.8" opacity="0.7" />
          </svg>
        ))}

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Sunburst medallion */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            overflow: "hidden",
            boxShadow: `0 0 0 4px ${bg}, 0 0 0 5px ${accent}66`,
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            fallback={
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `radial-gradient(circle at 50% 40%, ${accent}55 0%, ${accent}22 40%, ${bg} 80%)`,
                }}
              />
            }
          />
          <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} aria-hidden>
            {Array.from({ length: 18 }).map((_, i) => {
              const a = (i / 18) * Math.PI * 2;
              const x1 = 50 + Math.cos(a) * 42;
              const y1 = 50 + Math.sin(a) * 42;
              const x2 = 50 + Math.cos(a) * 48;
              const y2 = 50 + Math.sin(a) * 48;
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={accent} strokeWidth="0.5" opacity="0.5" />;
            })}
          </svg>
        </div>

        <div style={{ fontFamily: font, fontSize: 30, lineHeight: 1.05, fontWeight: 500, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
